/**
 * Principal Quick Call Scheduling
 * - Select observer and child
 * - Schedule a call for specific date/time
 * - View and manage scheduled calls
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Phone, User, Baby, Calendar, Clock, Plus, Play, 
  Trash2, RefreshCw, Loader2, CheckCircle, XCircle,
  AlertCircle
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PrincipalQuickSchedule = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [observers, setObservers] = useState([]);
  const [children, setChildren] = useState([]);
  const [schedules, setSchedules] = useState([]);
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    observer_id: '',
    child_id: '',
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 30,
    call_type: 'scheduled',
    notes: ''
  });

  const getToken = () => localStorage.getItem('principal_token');

  const loadData = async () => {
    try {
      setLoading(true);
      const token = getToken();
      
      const [observersRes, childrenRes, schedulesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/observers-calling-status?token=${token}`),
        axios.get(`${API_URL}/api/principal/children?token=${token}`),
        axios.get(`${API_URL}/api/principal/quick-schedules?token=${token}`)
      ]);
      
      // Only show observers with phone configured
      const configuredObservers = (observersRes.data.observers || []).filter(o => o.phone_configured);
      setObservers(configuredObservers);
      setChildren(childrenRes.data.children || childrenRes.data || []);
      setSchedules(schedulesRes.data.schedules || []);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSubmit = async () => {
    if (!formData.observer_id || !formData.child_id || !formData.scheduled_date || !formData.scheduled_time) {
      toast({ title: "Error", description: "Please fill all required fields", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      const token = getToken();
      
      // Combine date and time into ISO format
      const scheduledDatetime = `${formData.scheduled_date}T${formData.scheduled_time}:00`;
      
      const payload = {
        observer_id: formData.observer_id,
        child_id: formData.child_id,
        scheduled_datetime: scheduledDatetime,
        duration_minutes: formData.duration_minutes,
        call_type: formData.call_type,
        notes: formData.notes
      };
      
      const response = await axios.post(
        `${API_URL}/api/principal/quick-schedule?token=${token}`,
        payload
      );
      
      if (response.data.success) {
        toast({ title: "Success", description: "Call scheduled successfully" });
        setShowForm(false);
        setFormData({
          observer_id: '',
          child_id: '',
          scheduled_date: '',
          scheduled_time: '',
          duration_minutes: 30,
          call_type: 'scheduled',
          notes: ''
        });
        loadData();
      }
    } catch (err) {
      toast({ 
        title: "Error", 
        description: err.response?.data?.detail || "Failed to schedule call",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const triggerCall = async (scheduleId) => {
    try {
      const token = getToken();
      const response = await axios.post(
        `${API_URL}/api/principal/quick-schedule/${scheduleId}/trigger?token=${token}`
      );
      
      if (response.data.success) {
        toast({ title: "Call Triggered", description: "Call is being connected..." });
        loadData();
      } else {
        toast({ title: "Failed", description: response.data.message, variant: "destructive" });
      }
    } catch (err) {
      toast({ 
        title: "Error", 
        description: err.response?.data?.detail || "Failed to trigger call",
        variant: "destructive"
      });
    }
  };

  const cancelSchedule = async (scheduleId) => {
    if (!window.confirm('Cancel this scheduled call?')) return;
    
    try {
      const token = getToken();
      await axios.delete(`${API_URL}/api/principal/quick-schedule/${scheduleId}?token=${token}`);
      toast({ title: "Cancelled", description: "Scheduled call cancelled" });
      loadData();
    } catch (err) {
      toast({ title: "Error", description: "Failed to cancel", variant: "destructive" });
    }
  };

  const getStatusBadge = (status, autoTriggered = false) => {
    const autoIcon = autoTriggered ? '⚡' : '';
    switch (status) {
      case 'scheduled':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs flex items-center gap-1">
          <Clock className="w-3 h-3" /> Scheduled (Auto-dial)
        </span>;
      case 'triggered':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
          {autoIcon} Triggered {autoTriggered ? '(Auto)' : ''}
        </span>;
      case 'completed':
        return <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">Completed</span>;
      case 'cancelled':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">Cancelled</span>;
      case 'failed':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">Failed</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">{status}</span>;
    }
  };

  // Get today's date for min date in picker
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Schedule Call</h2>
          <p className="text-sm text-slate-500">Schedule ad-hoc calls between observers and children</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setShowForm(true)} className="bg-sanjaya-orange hover:bg-sanjaya-orange/90">
            <Plus className="w-4 h-4 mr-1" />
            Schedule Call
          </Button>
        </div>
      </div>

      {/* No Observers Warning */}
      {observers.length === 0 && !loading && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-yellow-600" />
            <div>
              <p className="font-medium text-yellow-800">No Observers with Phone Configured</p>
              <p className="text-sm text-yellow-600">Ask admin to configure observer phone numbers first.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Schedule Form */}
      {showForm && (
        <Card className="border-2 border-sanjaya-orange">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Schedule New Call
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Observer *</Label>
                <select
                  value={formData.observer_id}
                  onChange={(e) => setFormData({ ...formData, observer_id: e.target.value })}
                  className="mt-1 w-full h-10 px-3 border rounded-md"
                >
                  <option value="">Select Observer</option>
                  {observers.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.name} ({o.agent_number})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label>Child *</Label>
                <select
                  value={formData.child_id}
                  onChange={(e) => setFormData({ ...formData, child_id: e.target.value })}
                  className="mt-1 w-full h-10 px-3 border rounded-md"
                >
                  <option value="">Select Child</option>
                  {children.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                  min={today}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Time *</Label>
                <Input
                  type="time"
                  value={formData.scheduled_time}
                  onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Duration</Label>
                <select
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                  className="mt-1 w-full h-10 px-3 border rounded-md"
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>60 minutes</option>
                </select>
              </div>
              
              <div>
                <Label>Call Type</Label>
                <select
                  value={formData.call_type}
                  onChange={(e) => setFormData({ ...formData, call_type: e.target.value })}
                  className="mt-1 w-full h-10 px-3 border rounded-md"
                >
                  <option value="scheduled">Scheduled</option>
                  <option value="follow_up">Follow Up</option>
                  <option value="emergency">Emergency</option>
                </select>
              </div>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                placeholder="Optional notes about this call..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="mt-1"
                rows={2}
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSubmit} disabled={saving} className="bg-sanjaya-orange hover:bg-sanjaya-orange/90">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Calendar className="w-4 h-4 mr-1" />}
                Schedule Call
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
            
            {/* Auto-dial notice */}
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span><strong>Auto-Dial Enabled:</strong> Calls will automatically dial at the scheduled time. The observer's phone will ring first, then connect to the parent.</span>
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scheduled Calls List */}
      <Card>
        <CardHeader>
          <CardTitle>Scheduled Calls</CardTitle>
          <CardDescription>Calls auto-dial at scheduled time. You can also trigger manually with "Call Now".</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : schedules.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Calendar className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>No scheduled calls</p>
              <p className="text-sm">Click "Schedule Call" to create one</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Date & Time</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Observer</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Child</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Type</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Status</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map((schedule) => (
                    <tr key={schedule.id} className="border-b hover:bg-slate-50">
                      <td className="py-3 px-3">
                        <div className="font-medium">{schedule.scheduled_date}</div>
                        <div className="text-sm text-slate-500">
                          {schedule.scheduled_time_ist || schedule.scheduled_time} IST
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          {schedule.observer_name}
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <Baby className="w-4 h-4 text-slate-400" />
                          {schedule.child_name}
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <span className="capitalize">{schedule.call_type}</span>
                      </td>
                      <td className="py-3 px-3">
                        {getStatusBadge(schedule.status, schedule.auto_triggered)}
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex gap-1">
                          {schedule.status === 'scheduled' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => triggerCall(schedule.id)}
                                className="bg-green-600 hover:bg-green-700 text-white h-8"
                                title="Trigger call manually (will also auto-dial at scheduled time)"
                              >
                                <Play className="w-3 h-3 mr-1" />
                                Call Now
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => cancelSchedule(schedule.id)}
                                className="text-red-600 h-8"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                          {schedule.status === 'triggered' && (
                            <span className="text-green-600 flex items-center gap-1">
                              <Phone className="w-4 h-4 animate-pulse" />
                              {schedule.auto_triggered ? 'Auto-Dialing' : 'In Progress'}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PrincipalQuickSchedule;
